import { useEffect, useMemo, useRef } from 'react';
import { ChevronRight } from 'lucide-react';
import { MilestoneCardKind } from '@wix/ai-scheduling-assistant-common-lib/entities';
import { NavDestination, NavSection, resolveNavLink } from '@wix/ai-scheduling-assistant-common-lib/commonTypes';
import { useTranslation } from '../../lib/i18n';
import { useGenericBi } from '../../lib/use-generic-bi';
import { Typography, TextButton } from './ui';
import { fireConfetti } from './confetti';
import styles from './MilestoneCard.module.css';

/**
 * The in-chat "setup milestone reached" moment — rendered in place of a normal assistant bubble for a
 * row carrying `metadata.milestoneCard` (see `readMilestoneCard` in common-lib). It marks a workspace
 * being created, the website scan finishing, the home dashboard being ready, or the first routine going
 * live.
 *
 * ONE shell for every kind, matching iOS/Android 1:1 (`MilestoneCard.swift` / `MilestoneCard.kt`): a
 * lime check badge + bold title on a soft-green success surface, the detail as plain supporting copy,
 * and an inline "label ›" CTA. The copy is FIXED and localized per kind (never the caller-supplied
 * `name`/`subtitle`) except `workspace`'s header, which interpolates the real workspace name, and `plan`,
 * which shows the caller's `name`/`subtitle` and opens on a `nav`.
 *
 * On reveal it plays the green.mov reel once (unless `prefers-reduced-motion`): the card settles in
 * gently (a subtle scale + fade, no bounce), a lime ring draws itself around the badge, the disc fills in behind it with a soft halo
 * pulse, the check stroke draws itself in, then a delicate confetti burst of the Symphony particle shapes
 * (filled circles / rounded squares / rounded diamonds) pops up and to the right and fades away. This
 * lands as the newest message (see the ctx.pendingCards turn-end flush), so the moment plays where the
 * user is looking.
 */

type Props = { card: { kind: MilestoneCardKind; name?: string; subtitle?: string; nav?: NavDestination } };

/**
 * The header badge — Figma node 12481:61356: a solid lime disc (Accent 10, `--ds-green-neon-500`)
 * carrying a 16px Lucide `check` in the primary ink, inside a 24px frame. Resting (history / reduced
 * motion) it shows the settled disc + drawn check; on a live entrance the four refs play the green.mov
 * reel (ring draws around → disc fills with a halo pulse → check draws in), driven from `MilestoneCard`.
 * The check path is Lucide's `check` (`4,12 9,17 20,6`) scaled into the disc's inner 16px.
 */
const CHECK_PATH = 'M6.67 12 L10 15.33 L17.33 8';

export function MilestoneCard({ card }: Props) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const cardRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const haloRef = useRef<SVGCircleElement>(null);
  const discRef = useRef<SVGCircleElement>(null);
  const ringRef = useRef<SVGCircleElement>(null);
  const checkRef = useRef<SVGPathElement>(null);

  useEffect(() => {
    const el = cardRef.current;
    const canvas = canvasRef.current;
    const halo = haloRef.current;
    const disc = discRef.current;
    const ring = ringRef.current;
    const check = checkRef.current;
    const reduced = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    // Reduced motion (or no WAAPI): the card + badge are already settled via their default styles.
    if (reduced || !el || typeof el.animate !== 'function') {
      return;
    }
    // Card pop-in: a delicate settle — a subtle scale up (0.96 → 1) + fade + small 8px rise, no
    // overshoot/bounce — 420ms ease-out.
    el.animate(
      [
        { opacity: 0, transform: 'scale(0.96) translateY(8px)' },
        { opacity: 1, transform: 'scale(1) translateY(0)' },
      ],
      { duration: 420, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'both' },
    );
    // The green.mov badge reel: the ring draws itself around, the disc fills in behind it with a soft
    // halo pulse, then the check stroke draws itself in — each beat overlapping the next. Timings mirror
    // MilestoneCard.swift's `start()` (ring 0–420, disc 340–580, halo 420–1020, check 480–820).
    ring?.animate([{ strokeDashoffset: 1 }, { strokeDashoffset: 0 }], {
      duration: 420,
      easing: 'ease-in-out',
      fill: 'both',
    });
    ring?.animate([{ opacity: 1, offset: 0 }, { opacity: 1, offset: 0.59 }, { opacity: 0, offset: 1 }], {
      duration: 580,
      fill: 'both',
    });
    disc?.animate([{ opacity: 0, transform: 'scale(0.6)' }, { opacity: 1, transform: 'scale(1)' }], {
      duration: 240,
      delay: 340,
      easing: 'ease-out',
      fill: 'both',
    });
    halo?.animate([{ opacity: 0.3, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(1.8)' }], {
      duration: 600,
      delay: 420,
      easing: 'ease-out',
      fill: 'both',
    });
    check?.animate([{ strokeDashoffset: 1 }, { strokeDashoffset: 0 }], {
      duration: 340,
      delay: 480,
      easing: 'ease-in-out',
      fill: 'both',
    });
    // Confetti erupts once the check has drawn (native's bubble delay, 820ms), from the badge centre.
    const confettiTimer = window.setTimeout(() => {
      if (canvas && disc) {
        fireConfetti(canvas, disc.ownerSVGElement ?? disc);
      }
    }, 820);
    return () => window.clearTimeout(confettiTimer);
  }, []);

  const kind = card.kind;

  // The CTA needs a real destination. The marker's `nav` wins; a `dashboard` card without one still has
  // an unambiguous home to open. A `workspace`/`plan` card without a `nav` has no resolvable slug, so it
  // renders no CTA rather than a dead link.
  const navPath = useMemo(() => {
    const destination: NavDestination | undefined =
      card.nav ?? (kind === 'dashboard' ? { section: NavSection.Home } : undefined);
    return destination ? resolveNavLink(destination).desktop : undefined;
  }, [card.nav, kind]);

  // `workspace` interpolates the real name (nothing to name → body alone); `plan` shows the caller's
  // name; `dashboard`/`scan`/`phone` show their own fixed, localized header.
  const header =
    kind === 'workspace'
      ? card.name
        ? t('milestone.workspace.header').replace('{name}', card.name)
        : undefined
      : kind === 'plan'
        ? card.name || undefined
        : t(`milestone.${kind}.header`);

  // Every bubble kind shows its OWN fixed, localized body (`plan` shows the caller's subtitle), carrying
  // its own line breaks — rendered as one wrapping paragraph.
  const body = kind === 'plan' ? card.subtitle ?? '' : t(`milestone.${kind}.body`);

  // Figma renders a CTA for `workspace`/`dashboard`, none for `scan`/`phone` (nothing to open), and
  // `plan` opens on its `nav`.
  const ctaLabel =
    kind === 'workspace'
      ? t('milestone.workspace.cta')
      : kind === 'dashboard'
        ? t('milestone.dashboard.cta')
        : t('milestone.open');
  const showsCta = (kind === 'workspace' || kind === 'dashboard' || kind === 'plan') && !!navPath;

  const onCtaClick = () => logPageClickBi({ page_name: 'chat', button_name: 'milestone_open' });

  return (
    <div ref={cardRef} className={styles.bubble}>
      <canvas ref={canvasRef} aria-hidden className={styles.confetti} />
      <div className={styles.header}>
        <svg
          className={styles.badge}
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden
          focusable="false"
        >
          <circle ref={haloRef} className={styles.halo} cx="12" cy="12" r="12" />
          <circle ref={discRef} className={styles.disc} cx="12" cy="12" r="12" />
          <circle ref={ringRef} className={styles.ring} cx="12" cy="12" r="11" pathLength={1} />
          <path ref={checkRef} className={styles.check} d={CHECK_PATH} pathLength={1} />
        </svg>
        <div className={styles.text}>
          {header && (
            <Typography variant="h3" component="h3" color="strong" className={styles.title}>
              {header}
            </Typography>
          )}
          {body && (
            <Typography variant="p2" color="strong" className={styles.body}>
              {body}
            </Typography>
          )}
          {showsCta && navPath && (
            <TextButton
              size="small"
              href={navPath}
              onClick={onCtaClick}
              className={styles.cta}
              endIcon={<ChevronRight className="dir-flip" size={16} aria-hidden />}
            >
              {ctaLabel}
            </TextButton>
          )}
        </div>
      </div>
    </div>
  );
}
